import type { ReferencedFile } from "~/ai-chat/model/chat-message";
import { isReadableFileType } from "~/ai-chat/model/readable-file-types";
import type { EntityRef, ViewContextSlice } from "~/ai-chat/model/view-context";
import { serializeViewSlices } from "~/ai-chat/model/view-context";

const RESOURCE_TYPES = new Set(["resource", "model", "artifact", "file"]);

// The resource the user is looking at right now, if any — pulled from the live
// view slices (a file preview / focused resource) or the routed resource ids.
function resourceInView(context: ChatContext, viewSlices: ViewContextSlice[]): EntityRef | null {
  for (const slice of viewSlices) {
    if (slice.viewer?.kind === "file-preview") return slice.viewer.file;
    if (slice.selection?.focused && RESOURCE_TYPES.has(slice.selection.focused.type)) {
      return slice.selection.focused;
    }
    if (slice.primaryEntity && RESOURCE_TYPES.has(slice.primaryEntity.type)) {
      return slice.primaryEntity;
    }
  }
  if (context.modelId) return { id: context.modelId, name: "the model in view", type: "resource" };
  if (context.artifactId) return { id: context.artifactId, name: "the artifact in view", type: "artifact" };
  return null;
}

// The retrieval order-of-operations, scoped to what the user is currently on.
// The model must walk from the narrowest scope the page implies outward, and stop
// as soon as it finds the answer — never start with a platform-wide scan.
function serializeSearchOrder(context: ChatContext, viewSlices: ViewContextSlice[]): string[] {
  const resource = resourceInView(context, viewSlices);
  const header =
    "Order of operations for finding data — follow this for EVERY question about content, " +
    "data, or what exists. Start at the narrowest scope below and widen only if the answer " +
    "is not there; stop as soon as you find it. Never begin with a platform-wide or " +
    "workspace-wide search.";

  // For definitional / how-the-platform-works questions the answer is usually in the
  // system's own guide docs, not in tracked data — read those and answer from them.
  const definitionalGuidance =
    "For definitional or how-the-platform-works questions, consult the guide, readme, or " +
    "beginner-guide docs in the system and answer from them — do not decline with " +
    '"I can\'t verify" or "I don\'t want to guess" when such a guide doc exists.';

  if (resource) {
    return [
      header,
      `  1. Read the resource the user is viewing — ${resource.name} (${resource.type} ${resource.id}) — ` +
        "and answer from its contents. This is almost always what the question is about.",
      "  2. Only if the answer genuinely is not in that resource, widen to the rest of the platform.",
      "A resource's topic (people, pricing, company facts, anything) is NEVER a reason to skip reading it " +
        "or to say the information is unavailable — read it first, then answer.",
      definitionalGuidance,
    ];
  }
  if (context.systemId) {
    const branch = context.branchName
      ? `the current branch (${context.branchName}${context.branchId ? `, id ${context.branchId}` : ""})`
      : "the current branch";
    return [
      header,
      `  1. Look in ${branch} of the active system (${context.systemId}) — read the files tracked there.`,
      "  2. If not found, look across the active system's other branches.",
      "  3. Only if still not found, widen to the rest of the platform.",
      definitionalGuidance,
    ];
  }
  return [];
}

export interface ChatContext {
  // User
  userId: string;
  userName: string | null;
  isCustomerAdmin: boolean;

  // Navigation
  currentRoute: string;
  currentPage: string;

  // Resource IDs
  systemId?: string;
  branchId?: string;
  branchName?: string;
  modelId?: string;
  artifactId?: string;
  artifactVersionId?: string;
  artifactRevisionId?: string;
  jobId?: string;
  changeRequestId?: string;

  // UI State
  activeTab?: string;
  searchQuery?: string;
  compareWithId?: string;

  // Filters
  filters?: {
    type?: string;
    status?: string;
    creator?: string;
    tag?: string;
    resourceType?: string;
    mime?: string;
    extension?: string;
  };

  // Files the user attached to the conversation from the chat composer
  referencedFiles?: ReferencedFile[];
}

const EXACT_ROUTE_NAMES: Record<string, string> = {
  "/": "Home",
  "/sign-in": "Sign In",
  "/systems": "Systems",
  "/systems-dashboard": "Systems Dashboard",
  "/files": "Library",
  "/resources": "Library",
  "/jobs": "Jobs",
  "/admin": "Administration",
  "/admin/users": "User Administration",
  "/admin/functions": "Function Administration",
  "/admin/control-tags": "Control Tags Administration",
  "/admin/app-integrations": "App Integrations Administration",
  "/admin/integrations": "Integrations Administration",
  "/admin/public-key": "Public Key Administration",
  "/admin/encryption": "Encryption Administration",
  "/admin/encryption-keys": "Encryption Keys Administration",
  "/admin/infosec-levels": "Infosec Levels Administration",
  "/admin/infosec": "Infosec Administration",
  "/admin/usage": "Usage Administration",
  "/admin/compatibility": "Compatibility Administration",
  "/admin/secure-connections": "Secure Connections Administration",
  "/admin/ai": "AI Administration",
  "/settings": "Settings",
  "/settings/profile": "Profile Settings",
  "/settings/password": "Password Settings",
  "/settings/notifications": "Notification Settings",
  "/settings/application": "Application Settings",
  "/settings/developer": "Developer Settings",
  "/settings/ai-instructions": "AI Instructions Settings",
  "/settings/linked-accounts": "Linked Accounts Settings",
  "/comment": "Comments",
  "/documentation": "Documentation",
  "/magic-docs": "Magic Docs",
  "/upload": "Upload",
  "/agents": "Agents",
  "/notifications": "Notifications",
  "/search": "Search",
};

function matchSystemDashboardRoute(pathname: string): string {
  const commitDetailMatch = pathname.match(/\/commits\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (commitDetailMatch && commitDetailMatch[1] !== "changes-available") {
    return "Commit Detail";
  }
  if (pathname.includes("/directory")) return "System Directory";
  if (pathname.includes("/workflows")) return "System Workflows";
  if (pathname.includes("/branches")) return "System Branches";
  if (pathname.includes("/commits")) return "System Commits";
  if (pathname.includes("/change-requests/new")) return "Create Change Request";
  if (pathname.includes("/change-requests/")) return "Change Request Detail";
  if (pathname.includes("/change-requests")) return "System Change Requests";
  return "System Home";
}

function matchSystemRoute(pathname: string): string {
  if (pathname.includes("/change-requests/new")) return "Create Change Request";
  if (pathname.includes("/change-requests/")) return "Change Request Detail";
  return "System Detail";
}

export function getPageNameFromRoute(pathname: string, params: Record<string, string | undefined>): string {
  const exactMatch = EXACT_ROUTE_NAMES[pathname];
  if (exactMatch) return exactMatch;

  if (pathname.startsWith("/systems-dashboard/") && params.systemId) {
    return matchSystemDashboardRoute(pathname);
  }
  if (pathname.startsWith("/systems/") && params.systemId) {
    return matchSystemRoute(pathname);
  }
  if (pathname.startsWith("/files/artifact/") || pathname.startsWith("/resources/artifact/")) return "Artifact Detail";
  if (pathname.startsWith("/jobs/") && params.jobId) return "Job Detail";
  if (pathname.startsWith("/comment/")) return "Comment Detail";
  if (pathname.startsWith("/artifact/")) return "Artifact Detail";

  return pathname;
}

function serializeResourceIds(context: ChatContext): string[] {
  const lines: string[] = [];
  if (context.systemId) {
    lines.push(`Active System: ${context.systemId}`);
  }
  if (context.branchName) {
    lines.push(`Active Branch: ${context.branchName}`);
  }
  if (context.branchId) {
    lines.push(`Active Branch ID: ${context.branchId}`);
  }
  if (context.modelId) {
    lines.push(`Active Model: ${context.modelId}`);
  }
  if (context.artifactId) {
    lines.push(`Active Artifact: ${context.artifactId}`);
  }
  if (context.artifactVersionId) {
    lines.push(`Artifact Version: ${context.artifactVersionId}`);
  }
  if (context.artifactRevisionId) {
    lines.push(`Artifact Revision: ${context.artifactRevisionId}`);
  }
  if (context.jobId) {
    lines.push(`Active Job: ${context.jobId}`);
  }
  if (context.changeRequestId) {
    lines.push(`Change Request: ${context.changeRequestId}`);
  }
  return lines;
}

function serializeContentGuidance(context: ChatContext): string[] {
  const hasReadableFile = Boolean(
    context.modelId || context.artifactId || (context.referencedFiles && context.referencedFiles.length > 0),
  );
  const hasSystem = Boolean(context.systemId);
  if (!hasReadableFile && !hasSystem) return [];

  const lines = ["Answering questions about content:"];
  if (hasReadableFile) {
    lines.push(
      "  - The active model/artifact and any referenced files are Istari resources. To describe or " +
        "summarize a file, read the resource's contents by its id with your tools — never ask the user " +
        "to paste the file.",
    );
  }
  if (hasSystem) {
    lines.push(
      "  - To summarize or answer questions about the whole system, list the resources tracked in the active " +
        "system live by its id every time (do not rely on any earlier list or on this context), then read " +
        "each one.",
    );
  }
  return lines;
}

function serializeReferencedFiles(files: ReferencedFile[]): string[] {
  const lines = [
    "Referenced Resources (already uploaded and committed as Istari resources — operate on them directly by resource_id with your tools; do not re-create or re-upload them):",
  ];
  let hasUnreadable = false;
  for (const file of files) {
    const revision = file.fileRevisionId ? `, file_revision_id: ${file.fileRevisionId}` : "";
    const savedFromChat = file.origin === "chat-save" ? " (saved from this conversation)" : "";
    const unreadable = !isReadableFileType(file.name);
    if (unreadable) hasUnreadable = true;
    const readability = unreadable
      ? " — UNREADABLE CONTENT: you cannot read this file's contents, only reference it by resource_id"
      : "";
    lines.push(`  - ${file.name} — resource_id: ${file.resourceId}${revision}${savedFromChat}${readability}`);
  }
  if (hasUnreadable) {
    lines.push(
      "For any file marked UNREADABLE CONTENT: Istari can't extract its text yet. Do not summarize, quote, " +
        "or infer what is inside — tell the user you can't read that file type yet, and offer to act on it by id.",
    );
  }
  return lines;
}

export function serializeChatContext(context: ChatContext, viewSlices: ViewContextSlice[] = []): string {
  const lines: string[] = [
    "# Frontend Context",
    "The details below are a point-in-time snapshot of what the user is currently viewing in the Istari " +
      "web app. They may be stale: files, resources, systems, jobs, and members can change during the " +
      "conversation.",
    "Operating rules:",
    "  - Treat this context only as a pointer to what the user is looking at — never as the source of " +
      "truth for what currently exists.",
    "  - For any question about what exists, what changed, or a file's contents, query live with your " +
      "tools on every turn. Do not answer from this context, from cached state, or from earlier turns in " +
      "the conversation — data may have changed since.",
    "  - Never fabricate resource ids, file names, counts, revisions, or timestamps. If a tool returns " +
      "nothing or errors, say so plainly — do not guess or pattern-match a plausible-sounding answer.",
    "",
  ];

  lines.push(`Current Page: ${context.currentPage}`);
  lines.push(`Route: ${context.currentRoute}`);
  lines.push(`User: ${context.userName ?? "Unknown"} (${context.userId})`);

  if (context.isCustomerAdmin) {
    lines.push("Role: Customer Admin");
  }

  lines.push(...serializeResourceIds(context));

  // UI State
  if (context.activeTab) {
    lines.push(`Current Tab: ${context.activeTab}`);
  }
  if (context.searchQuery) {
    lines.push(`Search Query: ${context.searchQuery}`);
  }
  if (context.compareWithId) {
    lines.push(`Comparing With: ${context.compareWithId}`);
  }

  // Filters
  if (context.filters && Object.keys(context.filters).length > 0) {
    const filterPairs = Object.entries(context.filters)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => `${key}=${value}`)
      .join(", ");
    if (filterPairs) {
      lines.push(`Filters: ${filterPairs}`);
    }
  }

  if (context.referencedFiles && context.referencedFiles.length > 0) {
    lines.push(...serializeReferencedFiles(context.referencedFiles));
  }

  lines.push(...serializeContentGuidance(context));

  const searchOrder = serializeSearchOrder(context, viewSlices);
  if (searchOrder.length > 0) {
    lines.push("", ...searchOrder);
  }

  lines.push(...serializeViewSlices(viewSlices));

  return lines.join("\n");
}
